// src/api/botlinkd.ts

const CLOUD_MESSAGE_URL = "https://botlinkd.com/api/whatsapp/message";

// Media endpoints (attachments)
// NOTE: Backend must implement these endpoints.
const CLOUD_MEDIA_UPLOAD_URL = "https://botlinkd.com/api/whatsapp/media/upload";
const CLOUD_MEDIA_SEND_URL = "https://botlinkd.com/api/whatsapp/message/media";

export type CloudSendResponse = {
  success: boolean;
  data?: {
    mid: string;
    status: string;
  };
  error?: string;
};

export type CloudUploadResponse = {
  success: boolean;
  media_id?: string;
  mime?: string;
  size?: number;
  error?: string;
};

export type CloudSendMediaResponse = {
  success: boolean;
  data?: {
    mid: string;
    status: string;
  };
  error?: string;
};

export type CloudMediaType = "image" | "video" | "audio" | "document";

// Bootstrap info returned by /api/whatsapp/bootstrap
export interface BotlinkdBootstrap {
  display_phone: string;
  phone_number_id: string;
  waba_id: string;
  business_name: string | null;
  pusher: {
    key: string | null;
    cluster: string | null;
    channel: string;
  };
}

// Single chat summary from /api/whatsapp/chats
export interface BotlinkdChatSummary {
  id: string;
  name: string | null;
  phone: string | null;
  last_message: string | null;
  last_time: string | null;
  unread_count: number;
  auto_reply_enabled?: boolean; // AI toggle per chat (optional)
}

// Single message from /api/whatsapp/chat/{chatId}
export interface BotlinkdChatMessage {
  id: string;
  from_me: boolean;
  type?: string;          // NEW: message type (text, image, video, etc)
  body: string | null;    // Caption or text body
  media?: {               // NEW: Media details
    loaded: boolean;
    url: string | null;
    mime?: string;
  };
  status: string | null;
  created_at: string | null;
}

/**
 * Send a text message via BotLinkd Cloud API.
 * Mirrors the verify-all-api.php "Cloud API" panel behaviour.
 */
export async function sendCloudMessage(
  appkey: string,
  authkey: string,
  to: string,
  message: string
): Promise<CloudSendResponse> {
  const res = await fetch(CLOUD_MESSAGE_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      appkey,
      authkey,
      to,
      message,
    }),
  });

  if (!res.ok) {
    throw new Error(`Cloud send failed (${res.status})`);
  }

  const data = (await res.json()) as CloudSendResponse;
  return data;
}

/**
 * Upload a media file (image/video/audio/document) for WhatsApp.
 * Backend: POST /api/whatsapp/media/upload (multipart/form-data)
 */
export async function uploadCloudMedia(
  appkey: string,
  authkey: string,
  file: File
): Promise<CloudUploadResponse> {
  const form = new FormData();
  form.append("appkey", appkey);
  form.append("authkey", authkey);
  form.append("file", file);

  const res = await fetch(CLOUD_MEDIA_UPLOAD_URL, {
    method: "POST",
    body: form,
  });

  const data = (await res.json().catch(() => ({}))) as CloudUploadResponse;

  if (!res.ok || (data as any)?.error) {
    throw new Error((data as any)?.error || `Media upload failed (${res.status})`);
  }

  return data;
}

/**
 * Send a media message using an uploaded media_id.
 * Backend: POST /api/whatsapp/message/media
 */
export async function sendCloudMediaMessage(
  appkey: string,
  authkey: string,
  to: string,
  media_id: string,
  type: CloudMediaType,
  caption?: string
): Promise<CloudSendMediaResponse> {
  const res = await fetch(CLOUD_MEDIA_SEND_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      appkey,
      authkey,
      to,
      media_id,
      type,
      caption: caption || "",
    }),
  });

  const data = (await res.json().catch(() => ({}))) as CloudSendMediaResponse;
  if (!res.ok || (data as any)?.error) {
    throw new Error((data as any)?.error || `Media send failed (${res.status})`);
  }
  return data;
}

/**
 * Load WhatsApp Cloud bootstrap info for this appkey/authkey.
 * GET /api/whatsapp/bootstrap
 */
export async function fetchBootstrap(
  appkey: string,
  authkey: string
): Promise<BotlinkdBootstrap> {
  const url = `https://botlinkd.com/api/whatsapp/bootstrap?appkey=${encodeURIComponent(
    appkey
  )}&authkey=${encodeURIComponent(authkey)}`;

  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
  });

  const data = await res.json();

  if (!res.ok || (data && data.error)) {
    throw new Error(data?.error || "Failed to load WhatsApp bootstrap");
  }

  return data as BotlinkdBootstrap;
}

/**
 * Load chat list for this appkey/authkey.
 * GET /api/whatsapp/chats
 */
export async function fetchChats(
  appkey: string,
  authkey: string
): Promise<BotlinkdChatSummary[]> {
  const url = `https://botlinkd.com/api/whatsapp/chats?appkey=${encodeURIComponent(
    appkey
  )}&authkey=${encodeURIComponent(authkey)}`;

  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
  });

  const data = await res.json();

  if (!res.ok || (data && data.error)) {
    throw new Error(data?.error || "Failed to load WhatsApp chats");
  }

  return data as BotlinkdChatSummary[];
}

/**
 * Load messages for a specific chat.
 * GET /api/whatsapp/chat/{chatId}
 */
export async function fetchChatMessages(
  appkey: string,
  authkey: string,
  chatId: string,
  since?: string
): Promise<BotlinkdChatMessage[]> {
  const params = new URLSearchParams({
    appkey,
    authkey,
  });

  if (since) {
    params.append("since", since);
  }

  const url = `https://botlinkd.com/api/whatsapp/chat/${encodeURIComponent(
    chatId
  )}?${params.toString()}`;

  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
  });

  const data = await res.json();

  if (!res.ok || (data && data.error)) {
    throw new Error(data?.error || "Failed to load WhatsApp chat messages");
  }

  return data as BotlinkdChatMessage[];
}

/**
 * Response shape for AI toggle endpoint.
 */
export interface SetChatAiResponse {
  success: boolean;
  chat_id?: string;
  auto_reply_enabled?: boolean;
  error?: string;
}

/**
 * Enable/disable (or toggle) AI auto-reply for a conversation.
 *
 * Backend: POST /api/whatsapp/chat/{chatId}/ai
 * Body: { appkey, authkey, enabled? }
 */
export async function setChatAi(
  appkey: string,
  authkey: string,
  chatId: string | number,
  enabled?: boolean
): Promise<SetChatAiResponse> {
  const url = `https://botlinkd.com/api/whatsapp/chat/${encodeURIComponent(
    String(chatId)
  )}/ai`;

  const body: Record<string, any> = {
    appkey,
    authkey,
  };

  // If enabled is explicitly provided, send "on"/"off".
  // If omitted, backend will toggle.
  if (typeof enabled === "boolean") {
    body.enabled = enabled ? "on" : "off";
  }

  const res = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  const data = (await res.json().catch(() => ({}))) as SetChatAiResponse;

  if (!res.ok || data.error) {
    const msg = data.error || `Failed to toggle AI (status ${res.status})`;
    throw new Error(msg);
  }

  return data;
}

/**
 * Mark all messages in a given chat as read for this appkey/authkey.
 * POST /api/whatsapp/chat/{chatId}/read
 */
export async function markChatRead(
  appkey: string,
  authkey: string,
  chatId: string | number
): Promise<void> {
  const url = `https://botlinkd.com/api/whatsapp/chat/${encodeURIComponent(
    String(chatId)
  )}/read`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ appkey, authkey }),
  });

  if (!res.ok) {
    let message = "Failed to mark WhatsApp chat as read";
    try {
      const data = await res.json();
      if (data && data.error) {
        message = data.error;
      }
    } catch {
      // ignore JSON parse errors, keep generic message
    }
    throw new Error(message);
  }
}
